import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { EvidenceViewerComponent } from 'src/app/shared/evidence-viewer/evidence-viewer.component';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { StorageEndpoints } from 'src/app/core/resources/storage-endpoints';
import { Fmt } from 'src/app/core/messages/fmt';
import { ResourceMessages } from 'src/app/core/messages/resource-messages.enum';
import { FileService } from 'src/app/shared/files/file.service';
import { Global } from 'src/app/core/resources/global';

export interface InspectionDocumentData {
    file: File;
    url: string;
    type: 'image' | 'pdf';
}

@Component({
    selector: 'app-inspection-document',
    templateUrl: './inspection-document.component.html',
    styleUrls: ['./inspection-document.component.scss']
})
export class InspectionDocumentComponent implements OnInit {

    @ViewChild('documentFileInput') documentFileInput: ElementRef<HTMLInputElement>;

    @Input() inspectionType: 'container' | 'vehicle' = 'vehicle';
    @Input() settings?: { licensePlate?: string, cargoContainer?: string, cargoId?: string } = {};
    @Input() existingFileName: string;
    @Input() canUpload: boolean = true;
    @Output() fileSelected = new EventEmitter<InspectionDocumentData>();

    public fileToUpload: InspectionDocumentData;
    public uploadedFileUrl: string;
    public existingDocumentType: 'image' | 'pdf';

    constructor(
        private dialog: MatDialog,
        private snackBarService: SnackBarService,
        public utils: Utils,
        private fileService: FileService,
        private global: Global,
        private spinner: NgxSpinnerService
    ) { }

    ngOnInit() {
        if (this.existingFileName) {
            this.existingDocumentType = this.existingFileName.toLocaleLowerCase().endsWith('.pdf') ? 'pdf' : 'image';
            this.getDownloadURL(this.existingFileName).then(url => this.uploadedFileUrl = url);
        }
    }

    async getDownloadURL(fileName: string): Promise<string> {
        if (!fileName) return null;
        const path = Fmt.string(this.inspectionType === 'container' ? StorageEndpoints.cargoContainerChecklist : StorageEndpoints.cargoVehicleChecklist, this.settings.cargoId);
        const url = `${path}/${fileName}`;
        try {
            const response = await this.fileService.getURLDocument(url).toPromise();
            if (!response || response.code_ === "storage/object-not-found")
                return null;
            return response;
        } catch (error) {
            return null;
        }
    }

    public handleFileInput(e: Event & { target: { files: FileList } }) {
        if (!e || !e.target || !e.target.files || !e.target.files.length) return;
        const formatsAllowed = ['pdf', 'jpeg', 'jpg', 'png'];
        const file = e.target.files[0];
        const extension = file.name.split('.').reverse()[0];

        if (!formatsAllowed.includes(extension.toLocaleLowerCase()) || !file.type) {
            this.snackBarService.openSnackBar(`El archivo no es compatible con formatos ${formatsAllowed.join(', ')}`, 'x', 'error');
            return;
        }

        const newFile = this.utils.renameFile(file, `${this.inspectionType}-inspection-${new Date().getTime()}`);
        this.addFile(newFile, extension);

        if (this.documentFileInput && this.documentFileInput.nativeElement)
            this.documentFileInput.nativeElement.value = '';
    }

    private addFile(file: File, extension: string) {
        this.fileToUpload = {
            file,
            url: URL.createObjectURL(file),
            type: extension === 'pdf' ? 'pdf' : 'image',
        };
        this.fileSelected.emit(this.fileToUpload);
    }

    public openImagePreview(type: 'toUpload' | 'uploaded') {
        const dialogConfig = new MatDialogConfig();
        let evidences;

        if (type === 'toUpload' && this.fileToUpload)
            evidences = [{ url: this.fileToUpload.url, type: this.fileToUpload.type }];
        else if (type === 'uploaded' && this.uploadedFileUrl)
            evidences = [{ url: this.uploadedFileUrl, type: this.existingDocumentType }];
        else return;
        dialogConfig.data = { evidences, index: 0 };
        dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
        dialogConfig.width = ModalEnum.LARGE_WIDTH;
        dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
        dialogConfig.autoFocus = false;
        this.dialog.open(EvidenceViewerComponent, dialogConfig);
    }

    public async downloadDocument(): Promise<void> {
        if (!this.uploadedFileUrl) {
            this.snackBarService.openSnackBar('No hay documento disponible para descargar', undefined, 'alert');
            return;
        }

        try {
            // Mostrar spinner durante la descarga
            this.spinner.show();
            // Descargar el archivo desde Firebase usando fetch
            const response = await fetch(this.uploadedFileUrl);
            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }
            // Obtener el blob del archivo
            const blob = await response.blob();
            // Crear el nombre del archivo
            const inspectionTypeText = this.inspectionType === 'container' ? 'inspección-a-las-unidades-de-carga' : 'inspección-preoperacional-vehicular';
            const fileName = `<${this.settings.licensePlate}>${this.settings.cargoContainer ? '(' + this.settings.cargoContainer + ')' : ''} ${inspectionTypeText}`;
            // Usar el método downloadFile de utils que ya maneja la descarga correctamente
            this.utils.downloadFile(blob, fileName, blob.type);

        } catch (error) {
            this.spinner.hide();
            console.error('Error al descargar el documento:', error);
            this.snackBarService.openSnackBar(Fmt.string(ResourceMessages.ERROR_DOWNLOAD, 'el documento'), undefined, 'error');
        }
    }

    public getFileIcon(): string {
        if (!this.fileToUpload) return '';
        return this.fileToUpload.type === 'pdf' ? 'fas fa-file' : 'fas fa-image';
    }

    public getExistingFileIcon(): string {
        if (!this.uploadedFileUrl || !this.existingDocumentType) return '';
        return this.existingDocumentType === 'pdf' ? 'fas fa-file-pdf' : 'fas fa-image';
    }

    public onErrorRenderPdf($event: Event) {
        ($event.target as HTMLEmbedElement).src = this.global.pathNoImage;
    }
}
